import { authErrorMessage } from "@/auth/auth-error";
import { getApiRoot, getApiUrl } from "@/lib/api-url";
import { authClient, getAuthCallbackUrl } from "@/lib/auth-client";
import { completeDesktopSocialSignIn, desktopBridge } from "@/lib/desktop";
import { signupLanguageData } from "./signup-language";

const AUTHORIZE_PATH = "/auth/oauth2/authorize";

/** The pending MCP authorize query on /login, or null when there is none. */
function pendingAuthorizeQuery(search: string): string | null {
  const params = new URLSearchParams(search);
  const isAuthorizeQuery =
    params.get("client_id") &&
    params.get("redirect_uri") &&
    params.get("response_type");
  return isAuthorizeQuery ? params.toString() : null;
}

/**
 * The MCP OAuth authorize flow lands a signed-out user on /login with
 * the full OAuth query (client_id, redirect_uri, response_type, ...). Once a
 * session exists, the browser must go back to the API's authorize endpoint
 * with that exact query — as a top-level navigation, not an SPA route, so the
 * API can 302 onward to the consent page and finally the client's
 * redirect_uri. Returns null when the query is not an OAuth authorize resume.
 */
export function getOAuthAuthorizeResumeUrl(search: string): string | null {
  const query = pendingAuthorizeQuery(search);
  return query ? getApiUrl(`${AUTHORIZE_PATH}?${query}`) : null;
}

/**
 * The `callbackURL` a social (Google/Apple) sign-in should land on. When the
 * user is mid-MCP-connect — the authorize query is on /login — the provider
 * round-trip must resume to the API's authorize endpoint (→ consent → the
 * client's redirect_uri), the same handoff sign-in.tsx does for email/
 * password. Otherwise the normal post-sign-in landing. Social sign-in leaves
 * the SPA entirely, so this must be threaded through `callbackURL`: the
 * pending query is gone from the URL by the time the provider redirects back.
 */
export function getSocialSignInCallbackUrl(
  search: string,
  defaultCallbackUrl: string
): string {
  const query = pendingAuthorizeQuery(search);
  if (!query) {
    return defaultCallbackUrl;
  }
  // Better Auth redirects to the callback from the API's own host, so a
  // relative one names the API's path, not the app origin's `/api` mount.
  const apiSharesAppOrigin = getApiRoot().startsWith("/");
  const path = `${AUTHORIZE_PATH}?${query}`;
  return apiSharesAppOrigin ? path : getApiUrl(path);
}

/**
 * Start a redirect sign-in with Google or Apple, resolving to the message to
 * show when it could not start, or null once the browser is on its way.
 *
 * The desktop shell's loopback renderer can't navigate off-origin (and Google
 * refuses embedded webviews), so there the main process runs the flow in the
 * system browser and lands the session in Electron's jar.
 */
export async function redirectSocialSignIn(
  provider: "apple" | "google"
): Promise<string | null> {
  const bridge = desktopBridge();
  if (bridge) {
    const res = await completeDesktopSocialSignIn(
      bridge,
      provider,
      getAuthCallbackUrl()
    );
    return res.ok ? null : res.error;
  }
  try {
    const { error } = await authClient.signIn.social({
      callbackURL: getSocialSignInCallbackUrl(
        window.location.search,
        getAuthCallbackUrl()
      ),
      provider,
      ...signupLanguageData(window.location.search),
    });
    return error ? authErrorMessage(error) : null;
  } catch (err: unknown) {
    return authErrorMessage(err);
  }
}
